import {Strand} from 'constants/MolbioConstants';
import {PolymerFeatureType, VisualHistoryPolymerMapType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PolymerPreviewOverlay,
  PolymerPreviewOverlayType,
  VisualPolymer,
  VisualPolymerFeature,
  VisualPolymerFragment,
} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {VisualHistoryEventListItemFragment} from 'molbio/visual-history/__generated/VisualHistoryEventListItemFragment';

type VisualHistoryPolymerMapOverlayNode =
  VisualHistoryEventListItemFragment['eventVisualization']['overlays'][number];
type VisualHistoryPolymerFeatureNode =
  VisualHistoryEventListItemFragment['eventVisualization']['features'][number];
type VisualHistoryPolymerMapNode = VisualHistoryEventListItemFragment['eventVisualization'];

/**
 * Convert a visual history polymer feature node to a list of visual sequence fragments.
 * @param polymerFeature Visual history polymer feature to convert.
 */
function convertPolymerFeatureNodeToVisualSequenceFragments(
  polymerFeature: VisualHistoryPolymerFeatureNode
): Array<VisualPolymerFragment> {
  return polymerFeature.regions.map((region) => ({
    start: region.start,
    end: region.end,
    color: polymerFeature.color,
    homologyRegionOtherColor: polymerFeature.metadata?.homologyRegionOtherColor ?? null,
  }));
}

/**
 * Convert a visual history polymer feature node to a list of visual sequence features.
 * @param polymerFeature Visual history polymer feature to convert.
 */
function convertPolymerFeatureNodeToVisualSequenceFeatures(
  polymerFeature: VisualHistoryPolymerFeatureNode
): Array<VisualPolymerFeature> {
  return polymerFeature.regions.map((region) => ({
    name: polymerFeature.name,
    start: region.start,
    end: region.end,
    strand: polymerFeature.strand as Strand,
    color: polymerFeature.color,
  }));
}

/**
 * Convert a visual history polymer map overlay node to a polymer preview overlay.
 * @param polymerMapOverlay Visual history polymer map overlay to convert.
 */
function convertVisualHistoryPolymerMapOverlayNodeToPolymerPreviewOverlay(
  polymerMapOverlay: VisualHistoryPolymerMapOverlayNode
): PolymerPreviewOverlay {
  return {
    type: PolymerPreviewOverlayType[polymerMapOverlay.type],
    start: polymerMapOverlay.start,
    end: polymerMapOverlay.end,
    color: polymerMapOverlay.color ?? undefined,
  };
}

/**
 * Convert a visual history polymer map node to a visual sequence.
 * @param polymerMap Polymer map node to convert.
 */
export function convertPolymerMapNodeToVisualSequence(
  polymerMap: VisualHistoryPolymerMapNode
): VisualPolymer {
  return {
    circular: polymerMap.mapType === VisualHistoryPolymerMapType.CIRCULAR,
    fragments: polymerMap.features
      .filter((feature) => feature.type === PolymerFeatureType.FRAGMENT)
      .flatMap((feature) => convertPolymerFeatureNodeToVisualSequenceFragments(feature)),
    length: polymerMap.length,
    startIndex: polymerMap.startIndex,
    // TODO (BNCH-66055): Handle cut sites
    cutSites: [],
    annotations: polymerMap.features
      .filter((feature) => feature.type === PolymerFeatureType.ANNOTATION)
      .flatMap((feature) => convertPolymerFeatureNodeToVisualSequenceFeatures(feature)),
    overlays: polymerMap.overlays.map((overlay) =>
      convertVisualHistoryPolymerMapOverlayNodeToPolymerPreviewOverlay(overlay)
    ),
  };
}
